import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { SeoTags } from "../../components/Head/SeoTags";

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [content, setContent] = useState("");
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      try {
        // Load posts metadata
        const postsResponse = await fetch("/blog/posts.json");
        const postsData = await postsResponse.json();

        // Find the current post
        const currentPost = postsData.posts.find((p) => p.slug === slug);

        if (!currentPost) {
          navigate("/blog", { replace: true });
          return;
        }

        setPost(currentPost);

        // Load the markdown content
        const contentResponse = await fetch(`/blog/posts/${slug}.md`);
        if (contentResponse.ok) {
          const markdownContent = await contentResponse.text();
          setContent(markdownContent);
        }

        // Get related posts (same category, excluding current)
        const related = postsData.posts
          .filter((p) => p.category === currentPost.category && p.slug !== slug)
          .slice(0, 3);
        setRelatedPosts(related);
      } catch (error) {
        console.error("Failed to load blog post:", error);
        navigate("/blog", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const shareUrl = `https://onbookmarks.com/blog/${slug}`;

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-purple" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <>
      <SeoTags
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category}, bookmark tips, onbookmarks`}
        canonical={shareUrl}
        localized={false}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            author: {
              "@type": "Person",
              name: post.author,
            },
            datePublished: post.date,
            publisher: {
              "@type": "Organization",
              name: "onBookmarks",
              logo: {
                "@type": "ImageObject",
                url: "https://onbookmarks.com/images/logo.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": shareUrl,
            },
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://onbookmarks.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Blog",
                item: "https://onbookmarks.com/blog",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: post.title,
                item: shareUrl,
              },
            ],
          }),
        }}
      />

      {/* Breadcrumb */}
      <section className="py-3 bg-light border-bottom">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/blog">Blog</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {post.title}
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Article Header */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="mb-4">
                <span className="badge bg-purple-subtle text-purple mb-3">
                  {post.category}
                </span>
                <h1 className="display-5 fw-bold mb-3">{post.title}</h1>
                <p className="lead text-muted">{post.excerpt}</p>
              </div>

              <div className="d-flex align-items-center justify-content-between border-top border-bottom py-3 mb-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-purple-subtle rounded-circle d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                    <i className="bi bi-person text-purple"></i>
                  </div>
                  <div>
                    <div className="fw-medium">{post.author}</div>
                    <small className="text-muted">{formatDate(post.date)}</small>
                  </div>
                </div>

                {/* Share Buttons */}
                <div className="d-flex gap-2">
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary"
                    title="Share on Twitter"
                  >
                    <i className="bi bi-twitter"></i>
                  </a>
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary"
                    title="Share on LinkedIn"
                  >
                    <i className="bi bi-linkedin"></i>
                  </a>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigator.clipboard.writeText(shareUrl)}
                    title="Copy link"
                  >
                    <i className="bi bi-link-45deg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="pb-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <article className="blog-content">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => <h1 className="h2 fw-bold mt-5 mb-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="h3 fw-bold mt-5 mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="h4 fw-semibold mt-4 mb-3">{children}</h3>,
                    p: ({ children }) => <p className="mb-4">{children}</p>,
                    ul: ({ children }) => <ul className="mb-4">{children}</ul>,
                    ol: ({ children }) => <ol className="mb-4">{children}</ol>,
                    li: ({ children }) => <li className="mb-2">{children}</li>,
                    blockquote: ({ children }) => (
                      <blockquote className="border-start border-4 border-purple ps-4 py-2 my-4 bg-light rounded-end">
                        {children}
                      </blockquote>
                    ),
                    // react-markdown v10 dropped the `inline` prop, so infer it:
                    // fenced blocks carry a language- class or contain a newline;
                    // everything else is inline (e.g. a `tag` mid-sentence).
                    code: ({ className, children }) => {
                      const isBlock =
                        /language-/.test(className || "") ||
                        /\n/.test(String(children));
                      return isBlock ? (
                        <pre className="bg-dark text-light p-4 rounded mb-4 overflow-auto">
                          <code className={className}>{children}</code>
                        </pre>
                      ) : (
                        <code className="bg-light text-dark px-2 py-1 rounded small">
                          {children}
                        </code>
                      );
                    },
                    a: ({ href, children }) => (
                      <a href={href} className="text-purple" target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </article>

              {/* Author Box */}
              <div className="bg-light rounded-4 p-4 mt-5">
                <div className="d-flex gap-3">
                  <div className="bg-purple-subtle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: "60px", height: "60px" }}>
                    <i className="bi bi-person fs-4 text-purple"></i>
                  </div>
                  <div>
                    <h4 className="h6 mb-1">Written by {post.author}</h4>
                    <p className="text-muted small mb-0">
                      The onBookmarks team is dedicated to helping you organize your digital life better.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-purple rounded-4 p-4 text-center mt-5">
                <h3 className="h5 mb-3 text-white">Ready to organize your bookmarks?</h3>
                <p className="small mb-3 text-white">
                  Simplify your digital life with onBookmarks.
                </p>
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light"
                >
                  Get Started Free
                </Link>
              </div>

              {/* Quiet internal link to the migration guide on every post —
                  listicles and readers coming from Pocket's shutdown are the
                  blog's biggest switcher audience. */}
              <p className="text-muted small text-center mt-3 mb-0">
                Coming from Pocket?{" "}
                <Link to="/pocket-alternative" className="text-decoration-none">
                  Read the migration guide
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-5 bg-light">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto">
                <h2 className="h4 fw-bold mb-4">Related Articles</h2>
                <div className="row g-4">
                  {relatedPosts.map((relatedPost) => (
                    <div key={relatedPost.slug} className="col-md-6">
                      <article className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <span className="badge bg-purple-subtle text-purple mb-2">
                            {relatedPost.category}
                          </span>
                          <h3 className="h6 mb-2">
                            <Link
                              to={`/blog/${relatedPost.slug}`}
                              className="text-dark text-decoration-none"
                            >
                              {relatedPost.title}
                            </Link>
                          </h3>
                          <p className="text-muted small mb-0">
                            {formatDate(relatedPost.date)}
                          </p>
                        </div>
                      </article>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <section className="py-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <Link to="/blog" className="btn btn-outline-secondary">
                <i className="bi bi-arrow-left me-2"></i>
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPostPage;
